/**
 * Utilidades de validación genéricas.
 * Basado en Utilidades.java del legacy GWT.
 */

import { 
  REGEX_EMAIL, 
  REGEX_ONLY_LETTERS, 
  REGEX_ONLY_NUMBERS, 
  REGEX_ALPHANUMERIC,
  REGEX_PHONE,
  REGEX_IDENTIFICACION,
  REGEX_DECIMAL_NUMBERS,
  REGEX_HEX_COLOR,
  REGEX_DATES
} from './input-utils';

// ============================================
// VALIDACIONES DE FORMATO
// ============================================

/**
 * Valida si un valor tiene el formato de email válido
 */
export function isValidEmail(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  return REGEX_EMAIL.test(value);
}

/**
 * Valida si un valor contiene solo letras (y espacios/acentos)
 */
export function isValidLettersOnly(value: string | null | undefined): boolean {
  if (!value) {
    return true; // Vacío es válido (usar required aparte)
  }
  return REGEX_ONLY_LETTERS.test(value);
}

/**
 * Valida si un valor contiene solo números
 */
export function isValidNumbersOnly(value: string | null | undefined): boolean {
  if (!value) {
    return true;
  }
  return REGEX_ONLY_NUMBERS.test(value);
}

/**
 * Valida si un valor es alfanumérico válido
 */
export function isValidAlphanumeric(value: string | null | undefined): boolean {
  if (!value) {
    return true;
  }
  return REGEX_ALPHANUMERIC.test(value);
}

/**
 * Valida si un valor tiene formato de identificación válido
 */
export function isValidIdentificacion(value: string | null | undefined): boolean {
  if (!value) {
    return true;
  }
  return REGEX_IDENTIFICACION.test(value);
}

/**
 * Valida si un valor tiene formato de teléfono válido
 */
export function isValidPhone(value: string | null | undefined): boolean {
  if (!value) {
    return true;
  }
  return REGEX_PHONE.test(value);
}

/**
 * Valida si un valor tiene formato de número decimal válido
 */
export function isValidDecimal(value: string | null | undefined): boolean {
  if (!value) {
    return true;
  }
  return REGEX_DECIMAL_NUMBERS.test(value);
}

/**
 * Valida si un valor tiene formato de fecha válido (YYYY-MM-DD o YYYY/MM/DD)
 */
export function isValidDateFormat(value: string | null | undefined): boolean {
  if (!value) {
    return true;
  }
  return REGEX_DATES.test(value);
}

/**
 * Valida si un valor tiene formato de color hexadecimal
 */
export function isValidHexColor(value: string | null | undefined): boolean {
  if (!value) {
    return true;
  }
  return REGEX_HEX_COLOR.test(value);
}

// ============================================
// VALIDACIONES DE LONGITUD
// ============================================

/**
 * Valida si un valor no excede la longitud máxima
 */
export function isValidLength(value: string | null | undefined, maxLength: number): boolean {
  if (!value) {
    return true;
  }
  return value.length <= maxLength;
}

/**
 * Valida si un valor tiene al menos la longitud mínima
 */
export function isValidMinLength(value: string | null | undefined, minLength: number): boolean {
  if (!value) {
    return minLength === 0;
  }
  return value.length >= minLength;
}

/**
 * Valida si un valor está entre un rango de longitud
 */
export function isValidLengthRange(
  value: string | null | undefined, 
  minLength: number, 
  maxLength: number
): boolean {
  if (!value) {
    return minLength === 0;
  }
  return value.length >= minLength && value.length <= maxLength;
}

// ============================================
// VALIDACIONES DE CONTENIDO
// ============================================

/**
 * Valida si un valor es nulo, undefined o vacío
 * Equivale a isEmptyNull de Utilidades.java
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }
  return false;
}

/**
 * Valida si un valor NO es vacío (es "usable")
 * Equivale a isUsableData de Utilidades.java
 */
export function isNotEmpty(value: unknown): boolean {
  return !isEmpty(value);
}

/**
 * Valida si un valor es numérico
 * Equivale a isNumeric de Utilidades.java
 */
export function isNumeric(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'number') {
    return !isNaN(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return false;
    }
    return !isNaN(Number(trimmed));
  }
  return false;
}

// ============================================
// VALIDACIONES DE RANGO NUMÉRICO
// ============================================

/**
 * Valida si un número está en un rango
 */
export function isInRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

/**
 * Valida si un número es positivo
 */
export function isPositive(value: number): boolean {
  return value > 0;
}

/**
 * Valida si un número es no negativo (>= 0)
 */
export function isNonNegative(value: number): boolean {
  return value >= 0;
}

// ============================================
// VALIDACIÓN DE PAGINACIÓN
// ============================================

/** Límites por defecto para size en paginación de adapters HTTP. */
export const PAGE_SIZE_MIN = 1;
export const PAGE_SIZE_MAX = 100;

/**
 * Valida los parámetros page/size antes de enviarlos al backend.
 * Uso compartido entre adapters HTTP para no depender solo de la
 * validación del servidor (defensa en profundidad, seguridad-girop/SKILL.md §3).
 *
 * @param page Número de página (0-indexed), debe ser entero >= 0
 * @param size Tamaño de página, debe ser entero dentro de [min, max]
 * @param pageLabel Nombre del parámetro page en el mensaje de error (default 'page')
 * @param sizeLabel Nombre del parámetro size en el mensaje de error (default 'size')
 * @param min Tamaño mínimo permitido (default PAGE_SIZE_MIN)
 * @param max Tamaño máximo permitido (default PAGE_SIZE_MAX)
 * @throws Error si page o size no cumplen el rango esperado
 */
export function validarPageSize(
  page: number,
  size: number,
  pageLabel: string = 'page',
  sizeLabel: string = 'size',
  min: number = PAGE_SIZE_MIN,
  max: number = PAGE_SIZE_MAX
): void {
  if (!Number.isInteger(page) || page < 0) {
    throw new Error(`${pageLabel} debe ser mayor o igual a cero.`);
  }
  if (!Number.isInteger(size) || size < min || size > max) {
    throw new Error(`${sizeLabel} debe estar entre ${min} y ${max}.`);
  }
}

/**
 * Valida solo el tamaño de página, para adapters con paginación keyset/cursor (no tienen `page`).
 * Defensa en profundidad: no depender solo de la validación del servidor (seguridad-girop §2.3).
 *
 * @param size Tamaño de página, debe ser entero dentro de [min, max]
 * @param sizeLabel Nombre del parámetro en el mensaje de error (default 'size')
 * @throws Error si size no está en el rango esperado
 */
export function validarSize(
  size: number,
  sizeLabel: string = 'size',
  min: number = PAGE_SIZE_MIN,
  max: number = PAGE_SIZE_MAX
): void {
  if (!Number.isInteger(size) || size < min || size > max) {
    throw new Error(`${sizeLabel} debe estar entre ${min} y ${max}.`);
  }
}

// ============================================
// MENSAJES DE VALIDACIÓN
// ============================================

export const VALIDATION_MESSAGES = {
  required: 'Este campo es requerido',
  email: 'Formato de email inválido',
  minLength: (min: number) => `Mínimo ${min} caracteres`,
  maxLength: (max: number) => `Máximo ${max} caracteres`,
  pattern: 'Formato inválido',
  onlyLetters: 'Solo se permiten letras y espacios',
  onlyNumbers: 'Solo se permiten números',
  alphanumeric: 'Solo se permiten letras y números',
  identificacion: 'Solo se permiten números, guiones y puntos',
  phone: 'Formato de teléfono inválido',
  decimal: 'Formato de número decimal inválido',
  positive: 'El valor debe ser positivo',
  range: (min: number, max: number) => `El valor debe estar entre ${min} y ${max}`,
  date: 'Formato de fecha inválido',
  hexColor: 'Formato de color hexadecimal inválido (#RGB o #RRGGBB)'
};

// ============================================
// FUNCIÓN DE VALIDACIÓN COMPUESTA
// ============================================

export interface ValidationRule {
  type: 'required' | 'email' | 'minLength' | 'maxLength' | 'pattern' | 'range' | 'custom';
  value?: number | [number, number] | RegExp;
  message?: string;
  validator?: (value: unknown) => boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Valida un valor contra un conjunto de reglas
 */
export function validate(value: unknown, rules: ValidationRule[]): ValidationResult {
  const errors: string[] = [];

  for (const rule of rules) {
    switch (rule.type) {
      case 'required':
        if (isEmpty(value)) {
          errors.push(rule.message || VALIDATION_MESSAGES.required);
        }
        break;

      case 'email':
        if (isNotEmpty(value) && !isValidEmail(value as string)) {
          errors.push(rule.message || VALIDATION_MESSAGES.email);
        }
        break;

      case 'minLength':
        if (isNotEmpty(value) && !isValidMinLength(value as string, rule.value as number)) {
          errors.push(rule.message || VALIDATION_MESSAGES.minLength(rule.value as number));
        }
        break;

      case 'maxLength':
        if (isNotEmpty(value) && !isValidLength(value as string, rule.value as number)) {
          errors.push(rule.message || VALIDATION_MESSAGES.maxLength(rule.value as number));
        }
        break;

      case 'pattern':
        if (isNotEmpty(value) && !(rule.value as RegExp).test(value as string)) {
          errors.push(rule.message || VALIDATION_MESSAGES.pattern);
        }
        break;

      case 'range':
        const [min, max] = rule.value as [number, number];
        if (isNumeric(value) && !isInRange(Number(value), min, max)) {
          errors.push(rule.message || VALIDATION_MESSAGES.range(min, max));
        }
        break;

      case 'custom':
        if (rule.validator && !rule.validator(value)) {
          errors.push(rule.message || VALIDATION_MESSAGES.pattern);
        }
        break;
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}
